import { Box, Chip, Stack, Typography } from "@mui/material";
import CopyButton from "components/common/CopyButton";
import { CALIBRATE_FONT_FAMILY_MONO } from "components/hoc/Theme";
import { PASTEL_CHIP_BASE_SX } from "features/integrationsBeta/platformEnumUi";
import { humanizeKey } from "utils/humanizeKey";

/**
 * Renders a step's result payload / config dict in a user-friendly way:
 * - object keys are humanised (``downloaded_count`` → "Downloaded Count");
 * - primitive values render in the monospace stack so they read as data;
 * - arrays of primitives become bullet lists (no ``[0]``/``[1]`` noise);
 * - arrays of objects become numbered cards;
 * - booleans become pastel Yes/No chips.
 *
 * Step result dicts are arbitrary per integration, so this stays generic
 * rather than hard-coding any step's shape. Pass ``boxed`` (+ ``title``) to
 * frame the block as a labeled config container instead of loose rows.
 */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const MONO_VALUE_SX = {
  fontFamily: CALIBRATE_FONT_FAMILY_MONO,
  fontSize: 13,
  wordBreak: "break-word",
} as const;

function PrimitiveValue({
  value,
  copyable,
}: {
  value: unknown;
  copyable?: boolean;
}) {
  if (value === null || value === undefined || value === "") {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  if (typeof value === "boolean") {
    return (
      <Chip
        size="small"
        label={value ? "Yes" : "No"}
        sx={{
          ...PASTEL_CHIP_BASE_SX,
          height: 20,
          ...(value
            ? { bgcolor: "rgba(46,125,50,0.12)", color: "success.dark" }
            : { bgcolor: "rgba(0,0,0,0.08)", color: "text.secondary" }),
        }}
      />
    );
  }
  const rendered =
    typeof value === "number" ? value.toLocaleString() : String(value);
  const text = (
    <Typography component="span" variant="body2" sx={MONO_VALUE_SX}>
      {rendered}
    </Typography>
  );
  if (!copyable) return text;
  // Copyable mode: copy affordance sits OUTSIDE, to the left of the value's
  // bordered container so the value itself stays clean and selectable.
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        maxWidth: "100%",
      }}
    >
      <CopyButton value={String(value)} />
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 1,
          py: 0.25,
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

function ValueView({
  value,
  copyable,
}: {
  value: unknown;
  copyable?: boolean;
}) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <PrimitiveValue value={null} />;
    const allPrimitive = value.every(
      (v) => !isPlainObject(v) && !Array.isArray(v),
    );
    if (allPrimitive) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {value.map((v, i) => (
            <li key={i}>
              <PrimitiveValue value={v} copyable={copyable} />
            </li>
          ))}
        </Box>
      );
    }
    return (
      <Stack spacing={1}>
        {value.map((v, i) => (
          <Box
            key={i}
            sx={{ pl: 1.5, borderLeft: "2px solid", borderColor: "divider" }}
          >
            <Typography variant="caption" color="text.secondary">
              #{i + 1}
            </Typography>
            <ValueView value={v} copyable={copyable} />
          </Box>
        ))}
      </Stack>
    );
  }
  if (isPlainObject(value)) return <ObjectView obj={value} copyable={copyable} />;
  return <PrimitiveValue value={value} copyable={copyable} />;
}

function ObjectView({
  obj,
  copyable,
}: {
  obj: Record<string, unknown>;
  copyable?: boolean;
}) {
  const entries = Object.entries(obj);
  if (entries.length === 0) return <PrimitiveValue value={null} />;
  return (
    <Stack spacing={1.25}>
      {entries.map(([key, value]) => {
        const count = Array.isArray(value) ? ` (${value.length})` : "";
        const inline =
          !isPlainObject(value) && !Array.isArray(value) ? true : false;
        return (
          <Box
            key={key}
            sx={
              inline
                ? {
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }
                : undefined
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              sx={{ minWidth: inline ? 160 : undefined }}
            >
              {humanizeKey(key)}
              {count}
            </Typography>
            <Box sx={{ mt: inline ? 0 : 0.25 }}>
              <ValueView value={value} copyable={copyable} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

export default function PayloadView({
  value,
  emptyHint = "No details reported",
  boxed = false,
  title,
  copyable = false,
}: {
  value: unknown;
  emptyHint?: string;
  /** Frame the block as a labeled container (config panels). */
  boxed?: boolean;
  /** Caption shown above the content when ``boxed``. */
  title?: string;
  /** Render primitive values in bordered containers with a copy button. */
  copyable?: boolean;
}) {
  const empty =
    value === null ||
    value === undefined ||
    (isPlainObject(value) && Object.keys(value).length === 0) ||
    (Array.isArray(value) && value.length === 0);

  const body = empty ? (
    <Typography variant="body2" color="text.secondary">
      {emptyHint}
    </Typography>
  ) : (
    <ValueView value={value} copyable={copyable} />
  );

  if (!boxed) return body;

  return (
    <Box
      sx={{
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
      }}
    >
      {title && (
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing={0.6}
          display="block"
          sx={{ mb: 1 }}
        >
          {title}
        </Typography>
      )}
      {body}
    </Box>
  );
}
